import React, { useEffect, useMemo, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api, { downloadFile } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Loader, Btn, EmptyState, TextInput, SelectInput } from "../components/Primitives";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";

function StatCard({ label, value, tone = "primary", testId, hint }) {
  const c = tone === "danger" ? "var(--danger)" : tone === "success" ? "var(--success)"
          : tone === "warn" ? "var(--warn)" : "var(--primary)";
  return (
    <div className="surface rounded-xl p-4" style={{ border: "1px solid var(--border)" }}>
      <div className="text-[11px] font-semibold uppercase tracking-wider muted">{label}</div>
      <div data-testid={testId} className="text-2xl font-semibold mt-1" style={{ color: c }}>{value}</div>
      {hint && <div className="text-[11px] muted mt-1">{hint}</div>}
    </div>
  );
}

const ROLE_COLORS = {
  super_admin:     { bg: "var(--danger-soft)",  color: "var(--danger)"  },
  org_admin:       { bg: "var(--primary-soft)", color: "var(--primary)" },
  dpo:             { bg: "var(--primary-soft)", color: "var(--primary)" },
  legal_reviewer:  { bg: "var(--warn-soft)",    color: "var(--warn)"    },
  security_officer:{ bg: "var(--warn-soft)",    color: "var(--warn)"    },
  business_owner:  { bg: "var(--bg-2)",         color: "var(--muted)"   },
  vendor_manager:  { bg: "var(--bg-2)",         color: "var(--muted)"   },
  auditor:         { bg: "var(--success-soft)", color: "var(--success)" },
  data_principal:  { bg: "var(--bg-2)",         color: "var(--muted)"   },
  system:          { bg: "var(--bg-2)",         color: "var(--muted)"   },
};

function RolePill({ role }) {
  const t = ROLE_COLORS[role] || ROLE_COLORS.system;
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-[2px] text-[10px] font-medium rounded"
      style={{ background: t.bg, color: t.color }}>
      {role || "system"}
    </span>
  );
}

export default function AuditLogs() {
  const [tab, setTab] = useState("dashboard");
  const [dash, setDash] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chain, setChain] = useState(null);
  const [chainOpen, setChainOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const [fSearch, setFSearch] = useState("");
  const [fRole, setFRole] = useState("");
  const [fEntity, setFEntity] = useState("");
  const [fAction, setFAction] = useState("");

  const loadDash = async () => {
    try {
      const { data } = await api.get("/audit/dashboard");
      setDash(data);
    } catch { toast.error("Failed to load dashboard"); }
  };

  const loadList = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/audit/logs-v2", {
        params: { limit: 500, search: fSearch, actor_role: fRole, entity_type: fEntity, action: fAction },
      });
      setItems(data.items || []);
    } catch { toast.error("Failed to load logs"); }
    setLoading(false);
  };

  useEffect(() => { loadDash(); loadList();   }, []);
  useEffect(() => { if (tab === "list") loadList();   }, [fSearch, fRole, fEntity, fAction]);

  const verifyChain = async () => {
    toast.info("Verifying chain…");
    try {
      const { data } = await api.get("/audit/verify-chain");
      setChain(data); setChainOpen(true);
    } catch { toast.error("Failed"); }
  };

  const entityOptions = useMemo(() =>
    Array.from(new Set(items.map(i => i.entity_type).filter(Boolean))).sort(), [items]);
  const roleOptions = useMemo(() =>
    Array.from(new Set(items.map(i => i.actor_role).filter(Boolean))).sort(), [items]);

  if (!dash) return <Loader />;

  return (
    <div>
      <PageHeader
        title="Audit Logs"
        kicker="Append-only · SHA-256 hash-chained"
        description="Immutable trail of every privacy-critical action across all modules — with chain verification, role/entity aggregation, and evidence-grade PDF export."
        testId="audit-page-header"
        actions={
          <>
            <Btn variant="secondary" onClick={() => { loadDash(); loadList(); }} data-testid="audit-refresh">
              <Icons.RefreshCw className="w-3.5 h-3.5" /> Refresh
            </Btn>
            <Btn variant="secondary" onClick={verifyChain} data-testid="audit-verify">
              <Icons.ShieldCheck className="w-3.5 h-3.5" /> Verify chain
            </Btn>
            <button onClick={() => downloadFile("/audit/export.pdf", "audit_log.pdf")}
               className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-[11.5px] font-medium"
               style={{ background: "var(--bg-2)", color: "var(--ink)" }} data-testid="audit-pdf">
              <Icons.Download className="w-3.5 h-3.5" /> Export PDF
            </button>
          </>
        }
      />

      <div className="mb-5 flex items-center gap-1 surface rounded-lg p-1 w-fit" style={{ border: "1px solid var(--border)" }}>
        {[
          { key: "dashboard", label: "Dashboard", icon: "LayoutDashboard" },
          { key: "list", label: "Trail", icon: "ScrollText" },
        ].map(t => {
          const Icon = Icons[t.icon] || Icons.Circle;
          return (
            <button key={t.key} data-testid={`audit-tab-${t.key}`} onClick={() => setTab(t.key)}
              className="px-3 py-1.5 text-xs font-medium rounded-md flex items-center gap-1.5"
              style={{ background: tab === t.key ? "var(--primary-soft)" : "transparent", color: tab === t.key ? "var(--primary)" : "var(--muted)" }}>
              <Icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "dashboard" && (
        <div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <StatCard label="Total entries" value={dash.total_entries} testId="stat-total" />
            <StatCard label="Last 24 h" value={dash.entries_24h} tone="warn" testId="stat-24h" />
            <StatCard label="Last 7 d" value={dash.entries_7d} testId="stat-7d" />
            <StatCard label="Critical actions" value={dash.critical_actions} tone="danger" testId="stat-critical"
                     hint="delete / close / reject / notify / cert-in" />
            <StatCard label="Distinct actors" value={dash.distinct_actors} tone="success" testId="stat-actors" />
            <StatCard label="Distinct entities" value={dash.distinct_entities} testId="stat-entities" />
            <StatCard label="Last activity" value={(dash.last_activity_at || "").slice(0, 10) || "—"} testId="stat-last"
                     hint={(dash.last_activity_at || "").slice(11, 19)} />
            <StatCard label="Roles active" value={Object.keys(dash.by_role || {}).length} testId="stat-roles" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
            <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
              <div className="text-[13px] font-semibold ink mb-3">By role</div>
              <div className="space-y-1.5" data-testid="by-role">
                {Object.entries(dash.by_role || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-2 text-[11.5px]">
                    <RolePill role={k} />
                    <div className="flex-1" /><div className="muted">{v}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
              <div className="text-[13px] font-semibold ink mb-3">Top action groups</div>
              <div className="space-y-1.5" data-testid="top-actions">
                {(dash.top_actions || []).map(a => {
                  const max = Math.max(1, ...(dash.top_actions || []).map(x => x.count));
                  const pct = Math.round(a.count / max * 100);
                  return (
                    <div key={a.action}>
                      <div className="flex items-center gap-2 text-[11px]">
                        <span className="flex-1 ink mono truncate">{a.action}</span>
                        <span className="muted">{a.count}</span>
                      </div>
                      <div className="h-1.5 rounded-full overflow-hidden mt-0.5" style={{ background: "var(--bg-2)" }}>
                        <div className="h-full" style={{ width: `${pct}%`, background: "var(--primary)" }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
              <div className="text-[13px] font-semibold ink mb-3">Top entity types</div>
              <div className="space-y-1.5" data-testid="top-entities">
                {(dash.top_entities || []).map(e => (
                  <div key={e.entity} className="flex items-center gap-2 text-[11.5px]">
                    <Icons.Layers className="w-3 h-3" style={{ color: "var(--warn)" }} />
                    <div className="flex-1 ink truncate">{e.entity}</div>
                    <div className="muted">{e.count}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
              <div className="text-[13px] font-semibold ink mb-3">Top actors</div>
              <div className="space-y-1.5" data-testid="top-actors">
                {(dash.top_actors || []).map(a => (
                  <div key={a.actor} className="flex items-center gap-2 text-[11.5px]">
                    <Icons.User className="w-3 h-3" style={{ color: "var(--primary)" }} />
                    <div className="flex-1 ink truncate">{a.actor}</div>
                    <div className="muted">{a.count}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
              <div className="text-[13px] font-semibold ink mb-3">Daily activity (last 30 days)</div>
              <div className="space-y-1" data-testid="trend">
                {(dash.trend_by_day || []).slice(-14).map(t => {
                  const max = Math.max(1, ...(dash.trend_by_day || []).map(x => x.count));
                  const pct = Math.round(t.count / max * 100);
                  return (
                    <div key={t.day}>
                      <div className="flex items-center gap-2 text-[10.5px]">
                        <span className="flex-1 mono muted">{t.day}</span>
                        <span className="muted">{t.count}</span>
                      </div>
                      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "var(--bg-2)" }}>
                        <div className="h-full" style={{ width: `${pct}%`, background: "var(--success)" }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === "list" && (
        <div>
          <div className="mb-4 grid grid-cols-2 md:grid-cols-4 gap-2">
            <TextInput data-testid="filter-search" placeholder="Search action / actor / entity" value={fSearch} onChange={(e) => setFSearch(e.target.value)} />
            <SelectInput data-testid="filter-role" value={fRole} onChange={(e) => setFRole(e.target.value)}>
              <option value="">All roles</option>
              {roleOptions.map(r => <option key={r} value={r}>{r}</option>)}
            </SelectInput>
            <SelectInput data-testid="filter-entity" value={fEntity} onChange={(e) => setFEntity(e.target.value)}>
              <option value="">All entity types</option>
              {entityOptions.map(e => <option key={e} value={e}>{e}</option>)}
            </SelectInput>
            <TextInput data-testid="filter-action" placeholder="Action prefix (e.g. breach.)" value={fAction} onChange={(e) => setFAction(e.target.value)} />
          </div>
          {loading ? <Loader /> : items.length === 0 ? (
            <EmptyState title="No entries match" hint="Try widening your filters." />
          ) : (
            <div className="surface rounded-xl overflow-hidden" style={{ border: "1px solid var(--border)" }}>
              <table className="w-full text-sm">
                <thead style={{ background: "var(--bg-2)" }}>
                  <tr>
                    {["Timestamp", "Actor", "Role", "Action", "Entity", "Hash", ""].map(h => (
                      <th key={h} className="text-left px-4 py-2 text-[11px] font-semibold uppercase tracking-wider muted">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody data-testid="audit-tbody">
                  {items.map(l => (
                    <tr key={l.id} style={{ borderTop: "1px solid var(--border)" }} data-testid={`audit-row-${l.id}`}>
                      <td className="px-4 py-2 text-[10.5px] mono muted">{l.created_at?.slice(0, 19).replace("T", " ")}</td>
                      <td className="px-4 py-2 text-[11.5px] ink">{l.actor_name || "system"}</td>
                      <td className="px-4 py-2"><RolePill role={l.actor_role} /></td>
                      <td className="px-4 py-2 text-[11.5px] mono ink">{l.action}</td>
                      <td className="px-4 py-2 text-[10.5px] muted">
                        {l.entity_type || "—"}
                        {l.entity_id && <span className="ml-1 mono">· {l.entity_id.slice(0, 8)}</span>}
                      </td>
                      <td className="px-4 py-2 text-[10px] mono muted">{l.entry_hash?.slice(0, 10)}…</td>
                      <td className="px-4 py-2 text-right">
                        <button onClick={() => setDetail(l)} className="text-[11px] font-medium hover:underline"
                                style={{ color: "var(--primary)" }} data-testid={`audit-open-${l.id}`}>View</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* CHAIN VERIFICATION MODAL */}
      <Dialog open={chainOpen} onOpenChange={setChainOpen}>
        <DialogContent>
          {chain && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {chain.chain_valid ? (
                    <><Icons.ShieldCheck className="w-5 h-5" style={{ color: "var(--success)" }} /> Chain verified</>
                  ) : (
                    <><Icons.ShieldAlert className="w-5 h-5" style={{ color: "var(--danger)" }} /> Chain broken</>
                  )}
                </DialogTitle>
                <DialogDescription>
                  Verified {chain.verified} of {chain.total} entries at {chain.verified_at?.slice(0, 19).replace("T", " ")}.
                </DialogDescription>
              </DialogHeader>
              {chain.broken_count > 0 ? (
                <div className="max-h-[45vh] overflow-y-auto space-y-1.5">
                  {chain.broken.map(b => (
                    <div key={b.id} className="p-2 rounded-md" style={{ border: "1px solid var(--danger)", background: "var(--danger-soft)" }}>
                      <div className="text-[11.5px] ink mono">{b.action}</div>
                      <div className="text-[10px] muted">{b.created_at?.slice(0, 19).replace("T", " ")}</div>
                      <div className="text-[10.5px] mt-1">
                        Hash: {b.hash_matches ? "OK" : "MISMATCH"} · Chain: {b.chain_matches ? "OK" : "BROKEN"}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-[12px] ink p-3 rounded-md" style={{ background: "var(--success-soft)" }}>
                  All {chain.verified} entries have valid hashes and unbroken chain linkage. ✓
                </div>
              )}
              <DialogFooter>
                <Btn variant="secondary" onClick={() => setChainOpen(false)}>Close</Btn>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ENTRY DETAIL MODAL */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-2xl">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="mono text-[13px]">{detail.action}</DialogTitle>
                <DialogDescription>
                  {detail.created_at?.slice(0, 19).replace("T", " ")} · {detail.actor_name || "system"} · <RolePill role={detail.actor_role} />
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-2 text-[11.5px] max-h-[55vh] overflow-y-auto">
                {[
                  ["Entity type", detail.entity_type],
                  ["Entity ID", detail.entity_id],
                  ["Actor ID", detail.actor_id],
                  ["IP address", detail.ip_address],
                ].map(([k, v]) => (
                  <div key={k} className="p-2 rounded-md" style={{ background: "var(--bg-2)" }}>
                    <div className="text-[10px] font-semibold uppercase tracking-wider muted">{k}</div>
                    <div className="text-[11.5px] ink mt-0.5 mono truncate">{v || "—"}</div>
                  </div>
                ))}
                <div className="col-span-2 p-2 rounded-md" style={{ background: "var(--bg-2)" }}>
                  <div className="text-[10px] font-semibold uppercase tracking-wider muted">Old value</div>
                  <pre className="text-[10.5px] mono ink whitespace-pre-wrap mt-1">{detail.old_value ? JSON.stringify(detail.old_value, null, 2) : "—"}</pre>
                </div>
                <div className="col-span-2 p-2 rounded-md" style={{ background: "var(--bg-2)" }}>
                  <div className="text-[10px] font-semibold uppercase tracking-wider muted">New value</div>
                  <pre className="text-[10.5px] mono ink whitespace-pre-wrap mt-1">{detail.new_value ? JSON.stringify(detail.new_value, null, 2) : "—"}</pre>
                </div>
                <div className="col-span-2 p-2 rounded-md" style={{ background: "var(--bg-2)" }}>
                  <div className="text-[10px] font-semibold uppercase tracking-wider muted">Hash chain</div>
                  <div className="text-[10.5px] mono ink mt-1"><b>prev:</b> {detail.prev_hash || "—"}</div>
                  <div className="text-[10.5px] mono ink"><b>this:</b> {detail.entry_hash}</div>
                </div>
              </div>
              <DialogFooter>
                <Btn variant="secondary" onClick={() => setDetail(null)}>Close</Btn>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
